<script lang="ts" setup>
import { SancareOcticon } from '@sancare/typescript-commons'
import { capitalize } from '@sancare/typescript-commons/src/misc/string-utils'
import { HealthDataDocument } from '@sancare/typescript-commons/src/types/health-data'
import { Dayjs } from 'dayjs'
import { computed, ref } from 'vue'
import { useStore } from 'vuex'

import { StaySuggestionSearch, SuggestionJustification } from '@/misc/types'
import JustificationSummary from '@/stay-displayer/diagnosis-lists/ApiJustificationSummary.vue'
import {
  Diagnosis,
  DiagnosisType,
  getSingleDiagnosisTypes,
} from '@/store/modules/diagnosis/types'
import { PmsiTypeEnum } from '@/store/modules/settings/types'
import { StayHealthData } from '@/store/modules/stay/types'

const store = useStore()

const props = withDefaults(defineProps<{
  diagnosis: Diagnosis | null
  diagnosisType?: DiagnosisType | null
  confidence: number | null
  isSuggestion: boolean
  rhsId: number
  rhsEndDate: Dayjs
  searches?: StaySuggestionSearch[]
  healthData?: StayHealthData | null
  suggestionJustifications?: Record<string, SuggestionJustification> | null
}>(), {
  diagnosisType: null,
  searches: () => [],
  healthData: null,
  suggestionJustifications: null,
})

const removeDiagnosisRequest = ref({ fetching: false, error: null, ok: true })
const updateDiagnosisRequest = ref({ fetching: false, error: null, ok: true })
const isDropdownOpen = ref(false)
const showJustificationSummary = ref(false)

const isDiagnosisRequestFetching = computed(() => removeDiagnosisRequest.value.fetching || updateDiagnosisRequest.value.fetching)
const searchIds = computed(() => props.searches.map((search) => search.sancareId))
const searchNames = computed(() => props.searches.map((search) => search.name))

const dropdownButtons = computed(() => {
  const buttons = []

  for (const type of [...getSingleDiagnosisTypes(PmsiTypeEnum.SMR, props.rhsEndDate), DiagnosisType.DAS]) {
    if ((props.isSuggestion || type !== props.diagnosisType)
      && props.diagnosis[`canBe${capitalize(type.toLowerCase())}`]
    ) {
      const text: string = `${!props.isSuggestion ? displayMoveArrow(props.diagnosisType, type) : ''} Déplacer en ${type}`
      buttons.push({
        type: type,
        text: text,
        click: () => setDiagnosisAsType(type),
      })
    }
  }

  if (!props.isSuggestion) {
    buttons.push({
      type: 'delete',
      text: 'Supprimer',
      class: 'text-danger',
      click: () => removeDiagnosis(),
    })
  }

  return buttons
})

const apiJustification = computed(() => {
  if (!props.diagnosisType) {
    return null
  }
  return props.suggestionJustifications?.[`${props.diagnosisType}:${props.diagnosis.reference}`] ?? null
})
const canShowRational = computed(() => Boolean(apiJustification.value))

function makeSelection() {
  store.commit('smrStay/setJustificationSelection', {
    labelType: props.diagnosisType,
    labelReference: props.diagnosis.reference,
    savedSearchIds: searchIds.value,
  })
}

function toggleRationals() {
  showJustificationSummary.value = !showJustificationSummary.value
  if (showJustificationSummary.value) {
    makeSelection()
  }
}

function handleHealthDataSelection(mainContent: HealthDataDocument, scrollTarget: { docId: number, offset: number } | null = null) {
  store.commit('smrStay/setScrollTarget', scrollTarget)
  store.commit('smrStay/setMainContent', mainContent)
  makeSelection()
  toggleRationals()
}

function flagJustification(payload) {
  store.dispatch('smrStay/createFalseJustificationForSuggestion', payload)
}

function unflagJustification({ justificationId, docType, docId }) {
  store.dispatch('smrStay/deleteFalseJustification', {
    justificationId,
    reportId: docType === 'report' ? docId : null,
    healthEntryId: docType === 'textualHealthEntry' ? docId : null,
  })
}

function displayMoveArrow(currentType: DiagnosisType | null, moveToType: DiagnosisType | null) {
  return Object.values(DiagnosisType).indexOf(moveToType) < Object.values(DiagnosisType).indexOf(currentType)
    ? '↑'
    : '↓'
}

function displayDescription() {
  return getSingleDiagnosisTypes(PmsiTypeEnum.SMR, props.rhsEndDate).includes(props.diagnosisType)
    && !props.isSuggestion
}

const setDiagnosisAsType = async (type: DiagnosisType) => {
  isDropdownOpen.value = false
  updateDiagnosisRequest.value = { fetching: true, error: null, ok: false }
  let response: { error: string } | null

  if (!props.isSuggestion) {
    response = await store.dispatch('smrDiagnosis/updateSmrDiagnosis', {
      urlParams: {
        rhsId: props.rhsId,
        associatedDiagnosisId: props.diagnosis.id,
      },
      body: {
        currentDiagnosisType: props.diagnosisType,
        newDiagnosisType: type,
        diagnosis: props.diagnosis.reference,
      },
    })
  } else {
    response = await store.dispatch('smrDiagnosis/addSmrDiagnosis', {
      urlParams: {
        rhsId: props.rhsId,
      },
      body: {
        newDiagnosisType: type,
        diagnosis: props.diagnosis.reference,
      },
    })
  }

  if (response?.error) {
    updateDiagnosisRequest.value.error = response.error
  } else {
    updateDiagnosisRequest.value.ok = true
  }

  updateDiagnosisRequest.value.fetching = false
}

const removeDiagnosis = async () => {
  isDropdownOpen.value = false
  removeDiagnosisRequest.value = { fetching: true, error: null, ok: false }

  const response = await store.dispatch('smrDiagnosis/removeSmrDiagnosis', {
    urlParams: {
      rhsId: props.rhsId,
      associatedDiagnosisId: props.diagnosis.id,
    },
    body: {
      currentDiagnosisType: props.diagnosisType,
    },
  })

  if (response?.error) {
    removeDiagnosisRequest.value.error = response.error
  } else {
    removeDiagnosisRequest.value.ok = true
  }

  removeDiagnosisRequest.value.fetching = false
}

</script>

<template>
  <div
    class="diagnosis-line hoverable-item"
    :class="{
      highlighted: diagnosisType && isSuggestion,
      'is-opaque': isDiagnosisRequestFetching
    }"
  >
    <div class="d-flex justify-content-between">
      <div
        v-tooltip.left="{
          content: diagnosis.description,
          disabled: displayDescription(),
          delay: 0
        }"
        class="d-flex align-items-center"
      >
        <span
          v-if="diagnosisType && isSuggestion"
          class="badge text-bg-nosology me-1"
        >
          {{ diagnosisType }}
        </span>
        <strong>{{ diagnosis.reference }}</strong>
      </div>
      <div class="d-flex justify-content-between align-items-center">
        <v-dropdown
          :shown="showJustificationSummary"
          placement="left"
          class="popover-container flex-row align-items-center"
          popover-class="justification-popover"
          @auto-hide="showJustificationSummary = false"
        >
          <template v-if="confidence !== null">
            <sancare-octicon
              name="robot"
              :width="13"
              class="me-1"
            />
            <small
              class="text-end"
              style="width: 25px;"
            >
              {{ Math.round(confidence * 100) }}%
            </small>
          </template>
          <div
            v-if="searches.length"
            v-tooltip.left="{
              content: searches.length ? searchNames.join(',') : null,
              delay: 0
            }"
            class="d-flex align-items-center"
            @click="toggleRationals"
          >
            <sancare-octicon
              name="search"
              :width="13"
              :height="13"
              class="me-1"
            />
            <small
              class="diagnosis-searches"
              style="max-width: 80px;"
            >
              {{ searchNames.join(',') }}
            </small>
          </div>
          <template #popper>
            <justification-summary
              :is-loading="false"
              :justification="apiJustification"
              :health-data="healthData"
              :label-type="diagnosisType"
              :label-reference="diagnosis.reference"
              :no-value-label="canShowRational ? 'Justification non disponible en raison des paramètres' : 'Pas de justification'"
              @select-health-data="handleHealthDataSelection"
              @flag-justification="flagJustification"
              @unflag-justification="unflagJustification"
            />
          </template>
        </v-dropdown>
        <v-dropdown
          placement="top"
          class="popover-container ms-2"
          popover-class="rhs-diagnosis-settings-popover"
          :class="{ invisible: !dropdownButtons.length }"
          :shown="isDropdownOpen"
          @auto-hide="isDropdownOpen = false"
        >
          <sancare-octicon
            v-if="!isDiagnosisRequestFetching"
            :id="'diag-options-'+diagnosis.reference"
            name="kebab-horizontal"
            class="rhs-diagnosis-settings-icon"
            :width="18"
            :height="18"
            @click="isDropdownOpen = !isDropdownOpen"
          />
          <div
            v-else
            class="loader loader-xs loader-purple"
          />
          <template #popper>
            <button
              v-for="button in dropdownButtons"
              :key="`btn-${button.type}-${diagnosis.id}`"
              class="btn btn-link d-block"
              :class="button.class || ''"
              @click="button.click()"
            >
              {{ button.text }}
            </button>
          </template>
        </v-dropdown>
      </div>
    </div>
    <div v-if="displayDescription()">
      <small>{{ diagnosis['description'] }}</small>
    </div>
    <div
      v-if="removeDiagnosisRequest.error || updateDiagnosisRequest.error"
      class="error-block mt-2"
    >
      {{ removeDiagnosisRequest.error ?? updateDiagnosisRequest.error }}
    </div>
  </div>
</template>

<style lang="scss" scoped>
.diagnosis-line.is-opaque {
  opacity: .5;
}

.rhs-diagnosis-settings-icon {
  cursor: pointer;
}
</style>
